/**
 * What of /api/bootstrap goes into the SSR payload, and how the client gets
 * the rest. Used by app/plugins/02.store-initialization.ts and
 * app/plugins/07.pwa.client.ts.
 *
 * Every SSR page used to serialize the whole bootstrap into __NUXT_DATA__
 * (~1.99MB, the same bytes on every route) through the Pinia state. Two
 * pieces of that were never rendered on most routes:
 *
 *   - trails (~590KB) and points-of-interest (~150KB) are read during render
 *     only by the itinerary, neighbourhood and stop pages (see
 *     ROUTE_SCOPED below). Everywhere else they are left out of SSR and
 *     loaded client-side after hydration.
 *   - sites' "Submitted Photos" (~300KB) is synced but read by no component
 *     at all (grep app/ for it). It is dropped from the SSR copy only; the
 *     full bootstrap the client loads afterwards still carries it.
 *
 * Nothing that IS rendered changes, so server and client render the same
 * markup and hydration is unaffected; the site detail page keeps its
 * server-rendered title, h1 and description.
 */

export const BOOTSTRAP_KEYS = ['sites', 'neighborhoods', 'events', 'tours', 'trails', 'points-of-interest', 'sponsors', 'partners'] as const;

// Collection -> the routes whose SSR render reads it. If you make another
// page/component read trailsData or pointsOfInterestData during render, add
// its route here, or it will render empty on the server and mismatch.
export const ROUTE_SCOPED: Record<string, RegExp[]> = {
    // Templates/Tours/single.vue (itinerary detail), pages/stops/[...stop].vue
    trails: [/^\/(iframe\/)?itineraries\/[^/]/, /^\/stops\//],
    // Templates/Tours/single.vue, Templates/Neighborhoods/single.vue
    'points-of-interest': [/^\/(iframe\/)?itineraries\/[^/]/, /^\/(iframe\/)?neighborhoods\/[^/]/],
};

// Collection -> record fields no component reads.
export const UNUSED_FIELDS: Record<string, string[]> = {
    sites: ['Submitted Photos'],
};

function pathOnly(url: string): string {
    const p = (url || '/').split(/[?#]/)[0] || '/';
    return p.startsWith('/') ? p : '/' + p;
}

// Routes that render entirely from bootstrap data plus device-local My OHC
// state, so they work offline once the bootstrap is cached. The offline
// fallback page re-opens a requested URL only if it matches one of these;
// anything else (about pages, archive, admin) needs the network.
const OFFLINE_CAPABLE: RegExp[] = [
    /^\/$/,
    /^\/sites\/?$/,
    /^\/sites\/[^/]+\/?$/,
    /^\/events(\/[^/]+)?\/?$/,
    /^\/neighborhoods(\/[^/]+)?\/?$/,
    /^\/itineraries(\/[^/]+)?\/?$/,
    /^\/stops\/[^/]+\/?$/,
    /^\/my-ohc(\/[^/]+)?\/?$/,
];

export function isOfflineCapablePath(url: string | null | undefined): boolean {
    if (!url) return false;
    const path = pathOnly(url);
    if (path === '/offline') return false;
    return OFFLINE_CAPABLE.some((re) => re.test(path));
}

/** Bootstrap keys a route does NOT need for its server render. */
export function deferredKeysForPath(url: string): string[] {
    const path = pathOnly(url);
    return Object.entries(ROUTE_SCOPED)
        .filter(([, patterns]) => !patterns.some((re) => re.test(path)))
        .map(([key]) => key);
}

/** Bootstrap keys a client-side navigation to `url` needs loaded before it renders. */
export function requiredKeysForPath(url: string): string[] {
    const path = pathOnly(url);
    return Object.entries(ROUTE_SCOPED)
        .filter(([, patterns]) => patterns.some((re) => re.test(path)))
        .map(([key]) => key);
}

function withoutFields(records: any, fields: string[]): any {
    if (!Array.isArray(records) || !fields.length) return records;
    return records.map((r) => {
        if (!r || typeof r !== 'object' || !r.fields) return r;
        const copy = { ...r.fields };
        for (const f of fields) delete copy[f];
        return { ...r, fields: copy };
    });
}

/**
 * The part of a bootstrap `collections` map that goes into a server render
 * of `url`. Never mutates the input.
 */
export function ssrSubset(collections: Record<string, any> | undefined | null, url: string): { collections: Record<string, any>; deferred: string[] } {
    const deferred = deferredKeysForPath(url);
    const out: Record<string, any> = {};
    for (const [key, value] of Object.entries(collections ?? {})) {
        if (deferred.includes(key)) continue;
        const strip = UNUSED_FIELDS[key];
        out[key] = strip && value ? { ...value, data: withoutFields(value.data, strip) } : value;
    }
    return { collections: out, deferred };
}

/** A bootstrap response worth hydrating from: has a version and at least one collection. */
export function isUsableBootstrap(b: any): b is { version: string; collections: Record<string, any> } {
    return !!b && typeof b === 'object' && !!b.version && !!b.collections && Object.keys(b.collections).length > 0;
}

function storeHasData(store: any, key: string): boolean {
    // Every collection store keeps its version alongside the data (see
    // defineCollectionStore.ts applyCollectionResult); null means never hydrated.
    return !!store && store.version != null;
}

/**
 * Bootstrap keys whose store is missing or holds a different version than
 * `collections` carries. Comparing per collection, not the aggregate version,
 * is what lets a store left empty by a deferred SSR render still be filled by
 * a broadcast whose aggregate version matches what the page was rendered at.
 */
export function staleCollectionKeys(storesByKey: Record<string, any>, collections: Record<string, any> | undefined | null): string[] {
    if (!collections) return [];
    return Object.keys(storesByKey).filter((key) => {
        const incoming = collections[key];
        if (!incoming) return false;
        const store = storesByKey[key];
        if (!storeHasData(store, key)) return true;
        return (incoming.meta?.version ?? null) !== store.version;
    });
}

/** Keys from `keys` whose store has never been hydrated. */
export function missingKeys(storesByKey: Record<string, any>, keys: string[]): string[] {
    return keys.filter((key) => !storeHasData(storesByKey[key], key));
}

/**
 * Read /api/bootstrap straight out of the service worker's cache. The
 * fallback when the network fetch fails and the SW is not (yet) controlling
 * the page, e.g. the very first offline launch after an install.
 */
export async function readCachedBootstrap(cacheStorage: CacheStorage | undefined = (globalThis as any).caches, origin = (globalThis as any).location?.origin): Promise<any | null> {
    if (!cacheStorage || !origin) return null;
    try {
        const cache = await cacheStorage.open('ohc-bootstrap');
        const res = await cache.match(new URL('/api/bootstrap', origin).href, { ignoreSearch: true });
        if (!res) return null;
        const body = await res.json();
        return isUsableBootstrap(body) ? body : null;
    } catch {
        return null;
    }
}
